import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SocialLinks from '../../Components/SocialLinks/SocialLinks';
import './Homepage.css';

function Homepage() {
  useEffect(() => {
    const pageTitle = "Aunt Jack's Peanut Shack | Georgia-Grown Boiled Peanuts";
    const pageDescription =
      "Shop small-batch boiled peanuts or book Aunt Jack's Peanut Shack for markets, tailgates, and events across Northeast Georgia.";
    document.title = pageTitle;

    const updateMetaTag = (selector, attribute, value) => {
      const tag = document.head.querySelector(selector);
      if (tag) {
        tag.setAttribute(attribute, value);
      }
    };

    updateMetaTag('meta[name="description"]', 'content', pageDescription);
    updateMetaTag('meta[property="og:title"]', 'content', pageTitle);
    updateMetaTag('meta[property="og:description"]', 'content', pageDescription);
    updateMetaTag('meta[name="twitter:title"]', 'content', pageTitle);
    updateMetaTag('meta[name="twitter:description"]', 'content', pageDescription);

    const structuredDataId = 'homepage-structured-data';
    const existingStructuredData = document.getElementById(structuredDataId);
    if (existingStructuredData) {
      existingStructuredData.remove();
    }

    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'FoodEstablishment',
      name: "Aunt Jack's Peanut Shack",
      image: 'https://www.auntjackspeanutshack.com/images/auntjacks-logo.png',
      description: pageDescription,
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Colbert',
        addressRegion: 'GA',
        addressCountry: 'US',
      },
      servesCuisine: ['Southern', 'Cajun'],
      areaServed: {
        '@type': 'AdministrativeArea',
        name: 'Northeast Georgia',
      },
      url: 'https://www.auntjackspeanutshack.com/',
      sameAs: [
        'https://www.facebook.com/aunt.jacks.peanut.shack',
        'https://www.instagram.com/aunt_jack_peanut/',
      ],
      telephone: '+1-706-248-5702',
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = structuredDataId;
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, []);

  return (
    <div className='homepage'>
      <section className='hero-section'>
        <div className='hero-content'>
          <p className='hero-eyebrow'>Since 2013 · Colbert, Georgia</p>
          <h1>Boiled peanuts that taste like Saturday afternoons in the South.</h1>
          <p className='hero-description'>
            Aunt Jack&apos;s Peanut Shack brings slow-simmered, perfectly seasoned peanuts to festivals, football tailgates, and
            front-porch gatherings across northeast Georgia. Order your favorite flavors or invite us to your next event.
          </p>
          <div className='hero-actions'>
            <Link className='button primary' to={'/shop'}>
              Order a batch
            </Link>
            <Link className='button ghost' to={'/events'}>
              Where we&apos;ll be next
            </Link>
          </div>
        </div>
      </section>

      <section className='story-section' id='story'>
        <div className='story-card'>
          <h2>Steaming bags. Smiling faces.</h2>
          <p>
            Every boil starts with Georgia-grown peanuts, fresh aromatics, and a copper kettle. We slow cook each batch until the
            shells crack just right, then serve them piping hot with a grin and a little hometown storytelling.
          </p>
          <ul className='story-highlights'>
            <li>Small-batch boils made the morning of your event</li>
            <li>Family recipes with Original, Bayou Cajun, and Dill Pickle favorites</li>
            <li>Frozen gallon preorders for parties, reunions, and tailgates</li>
          </ul>
        </div>
        <div className='story-image' role='img' aria-label='Fresh boiled peanuts being seasoned in a kettle.' />
      </section>

      <section className='flavors-section'>
        <div className='section-heading'>
          <h2>Choose your flavor</h2>
          <p>Whether you like it classic or crave a Cajun kick, there&apos;s a bag with your name on it.</p>
        </div>
        <div className='flavor-grid'>
          <div className='flavor-card'>
            <img
              src='/images/dill-pickle.jpg'
              alt='Dill pickle boiled peanuts'
              loading='lazy'
              width='480'
              height='320'
            />
            <div className='flavor-info'>
              <h3>Dill Pickle</h3>
              <p>Tangy, bright, and just the right amount of garlicky.</p>
            </div>
          </div>
          <div className='flavor-card'>
            <img
              src='/images/cajun.jpg'
              alt='Bayou Cajun boiled peanuts'
              loading='lazy'
              width='480'
              height='320'
            />
            <div className='flavor-info'>
              <h3>Bayou Cajun</h3>
              <p>Spicy, smoky, and bold enough for your biggest game day crew.</p>
            </div>
          </div>
          <div className='flavor-card'>
            <img
              src='/images/original.jpg'
              alt='Original boiled peanuts'
              loading='lazy'
              width='480'
              height='320'
            />
            <div className='flavor-info'>
              <h3>Original</h3>
              <p>Salted just right for purists who know tradition never goes out of style.</p>
            </div>
          </div>
        </div>
        <Link className='section-cta' to={'/shop'}>
          Browse the full menu
        </Link>
      </section>

      <section className='events-highlight'>
        <div className='events-card'>
          <h2>Catch us out in the wild</h2>
          <p>
            From farmers markets to downtown festivals, Aunt Jack&apos;s pops up where the community gathers. Check the calendar to see
            when we&apos;ll be near you and snag a bag while it&apos;s hot.
          </p>
          <div className='event-promo-list'>
            <div className='event-promo'>
              <span className='event-dot' />
              <p>Weekly markets across Athens, Madison County and Oconee County</p>
            </div>
            <div className='event-promo'>
              <span className='event-dot' />
              <p>High school tailgates and collegiate game days</p>
            </div>
            <div className='event-promo'>
              <span className='event-dot' />
              <p>Private parties, fundraisers, and corporate treats</p>
            </div>
          </div>
          <Link className='button outline' to={'/events'}>
            View upcoming events
          </Link>
        </div>
      </section>

      <section className='social-section' aria-labelledby='social-heading'>
        <div className='social-card'>
          <div>
            <p className='social-eyebrow'>Fresh from the shack</p>
            <h2 id='social-heading'>Follow along for the next hot batch</h2>
            <p>
              Find pop-up announcements, behind-the-scenes boils, and plenty of peanut goodness on Facebook and Instagram.
            </p>
          </div>
          <SocialLinks variant='homepage' />
        </div>
      </section>

      <section className='contact-section'>
        <div className='contact-card'>
          <div className='contact-details'>
            <h2>Let&apos;s plan something delicious</h2>
            <p>
              Want gallons for the freezer or a cart at your next event? Jackie would love to chat details and reserve your date.
              Reach out and we&apos;ll start prepping a custom boil.
            </p>
            <div className='contact-info-grid'>
              <div>
                <span className='contact-label'>Call Jackie</span>
                <Link className='contact-link' to={'tel:706-248-5702'}>
                  706-248-5702
                </Link>
              </div>
              <div>
                <span className='contact-label'>Email</span>
                <Link className='contact-link' to={'mailto:carjack@myyahoo.com'}>
                  carjack@myyahoo.com
                </Link>
              </div>
            </div>
          </div>
          <div className='contact-image'>
            <img
              src='/images/shack1.jpg'
              alt='Owner Jackie serving boiled peanuts'
              loading='lazy'
              width='640'
              height='480'
            />
          </div>
        </div>
      </section>
    </div>
  );
}

export default Homepage;
